import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ChevronDown, ExternalLink, Layers } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePlanLens } from "@/global/PlanLensContext";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/formatters";

const SEGMENT =
  "text-muted-foreground hover:bg-muted flex h-full items-center gap-1.5 px-2.5 text-sm font-medium not-first:border-l first:rounded-l-md last:rounded-r-md";
const ACTIVE = "bg-accent text-accent-foreground";

function RecalcDot({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      role="img"
      aria-label={t("planLens.needsRecalc")}
      title={t("planLens.needsRecalc")}
      className={cn("size-1.5 shrink-0 rounded-full bg-amber-500", className)}
    />
  );
}

export function PlanLens() {
  const { t } = useTranslation();
  const { run, runs, setRunId } = usePlanLens();
  const runLabel = (r: { id: number; planDate: string }) =>
    t("planLens.run", { id: r.id, planDate: formatDate(r.planDate) });

  return (
    <div className="ml-auto flex h-8 items-center rounded-md border shadow-xs">
      <button
        type="button"
        className={cn(SEGMENT, !run && ACTIVE)}
        onClick={() => setRunId(null)}
      >
        <Layers className="size-3.5" />
        {t("planLens.withoutPlan")}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger className={cn(SEGMENT, run && ACTIVE)}>
          {run ? runLabel(run) : t("planLens.title")}
          {run?.needsRecalc && <RecalcDot />}
          <ChevronDown className="size-3.5 opacity-60" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuRadioGroup
            value={run ? String(run.id) : ""}
            onValueChange={(value) => setRunId(Number(value))}
          >
            {runs.map((r) => (
              <DropdownMenuRadioItem key={r.id} value={String(r.id)}>
                {runLabel(r)}
                {r.needsRecalc && <RecalcDot className="ml-auto" />}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {run && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={`/planning/runs/${run.id}`}
              aria-label={t("planLens.openRun", { id: run.id })}
              className={SEGMENT}
            >
              <ExternalLink className="size-3.5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            {t("planLens.openRun", { id: run.id })}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
